import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import toast from "react-hot-toast";

import {
  FaWhatsapp,
  FaCopy,
  FaRedoAlt,
  FaBullhorn,
  FaImage,
  FaCalendarAlt,
  FaInstagram,
  FaFacebookF,
  FaPaperPlane,
  FaMagic,
  FaEdit,
  FaCheckCircle,
  FaShareAlt,
  FaLayerGroup,
  FaArrowRight,
  FaPalette,
  FaMobileAlt,
  FaBolt,
  FaRegSmileBeam,
  FaClock,
  FaChevronRight,
  FaSave,
  FaEye,
  FaTimes,
} from "react-icons/fa";

import {
  useGenerateDailyMessageMutation,
  useSaveDailyMessageTemplateMutation,
} from "../toolkitServices/dailyMessageApi";

/* =========================================================
   ANIMATIONS
========================================================= */

const pageVariant = {
  hidden: {
    opacity: 0,
  },

  visible: {
    opacity: 1,

    transition: {
      staggerChildren: 0.05,
    },
  },
};

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 12,
  },

  visible: {
    opacity: 1,
    y: 0,

    transition: {
      duration: 0.35,
      ease: "easeOut",
    },
  },
};

const workspaceVariant = {
  initial: {
    opacity: 0,
    y: 8,
    scale: 0.995,
  },

  animate: {
    opacity: 1,
    y: 0,
    scale: 1,

    transition: {
      duration: 0.24,
      ease: "easeOut",
    },
  },

  exit: {
    opacity: 0,
    y: -6,
    scale: 0.995,

    transition: {
      duration: 0.16,
    },
  },
};

/* =========================================================
   SPINNER
========================================================= */

const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`
      inline-block
      animate-spin
      rounded-full
      border-2
      border-current
      border-t-transparent
      ${className}
    `}
    style={{
      width: size,
      height: size,
    }}
  />
);

/* =========================================================
   MAIN PAGE
========================================================= */

const Marketing = () => {
  const editorRef = useRef(null);

  const [activeTab, setActiveTab] = useState("messages");

  const [generatedMessage, setGeneratedMessage] = useState("");

  const [originalMessage, setOriginalMessage] = useState("");

  const [rateBlock, setRateBlock] = useState("");

  const [hasSavedTemplate, setHasSavedTemplate] = useState(false);

  const [generateDailyMessage, { isLoading: messageLoading }] =
    useGenerateDailyMessageMutation();

  const [saveDailyMessageTemplate, { isLoading: templateSaving }] =
    useSaveDailyMessageTemplateMutation();

  /* =========================================================
     STUDIO MENUS
  ========================================================= */

  const studioMenus = [
  {
    key: "messages",
    title: "Daily Rate",
    shortTitle: "Daily Rate",
    subtitle: "Gold & Silver rate sharing",
    description:
      "Create today's Gold & Silver rate message, customize the wording and share it with customers.",
    icon: FaWhatsapp,
    status: "Live",
  },

  {
    key: "posts",
    title: "Daily Post",
    shortTitle: "Daily Post",
    subtitle: "Daily branded creatives",
    description:
      "Create beautiful branded daily posts, greetings and social creatives for your jewellery business.",
    icon: FaImage,
    status: "Soon",
  },

  {
    key: "leads",
    title: "Leads",
    shortTitle: "Leads",
    subtitle: "Marketing enquiries",
    description:
      "Manage customer enquiries coming from Instagram, Facebook, WhatsApp, exhibitions and other marketing channels.",
    icon: FaBullhorn,
    status: "Soon",
  },
];

  const activeMenu = studioMenus.find((menu) => menu.key === activeTab);

  /* =========================================================
     LOAD DAILY MESSAGE
  ========================================================= */

  const loadDailyMessage = async (showToast = false) => {
    try {
      const response = await generateDailyMessage({
        additionalMessage: "",
      }).unwrap();

      const message = response?.data?.message || "";
      const currentRateBlock = response?.data?.rateBlock || "";
      const saved = response?.data?.hasSavedTemplate || false;

      if (!message) {
        toast.error("Unable to generate today's message");
        return;
      }

      setGeneratedMessage(message);
      setOriginalMessage(message);
      setRateBlock(currentRateBlock);
      setHasSavedTemplate(saved);

      if (showToast) {
        toast.success(
          saved
            ? "Saved format restored with today's latest rates"
            : "Default message restored",
        );
      }
    } catch (error) {
      console.error("Daily message error:", error);

      toast.error(
        error?.data?.msg ||
          error?.data?.message ||
          "Failed to generate daily rate message",
      );
    }
  };

  /* =========================================================
     PAGE LOAD
  ========================================================= */

  useEffect(() => {
    loadDailyMessage(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================================================
     EDIT STATUS
  ========================================================= */

  const isEdited = useMemo(() => {
    if (!originalMessage) {
      return false;
    }

    return generatedMessage !== originalMessage;
  }, [generatedMessage, originalMessage]);

  /* =========================================================
     SAVE FOR FUTURE
  ========================================================= */

  const handleSaveForFuture = async () => {
    if (!generatedMessage?.trim()) {
      toast.error("No message available");
      return;
    }

    if (!rateBlock?.trim()) {
      toast.error("Today's rate information is not available");
      return;
    }

    try {
      await saveDailyMessageTemplate({
        message: generatedMessage,
        rateBlock,
      }).unwrap();

      setOriginalMessage(generatedMessage);
      setHasSavedTemplate(true);

      toast.success(
        "Saved! This format will be used automatically for future daily messages.",
      );
    } catch (error) {
      console.error("Save message format error:", error);

      toast.error(
        error?.data?.msg ||
          error?.data?.message ||
          "Failed to save message format",
      );
    }
  };

  /* =========================================================
     COPY
  ========================================================= */

  const handleCopy = async () => {
    if (!generatedMessage) {
      toast.error("No message available");
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedMessage);

      toast.success("Message copied");
    } catch (error) {
      console.error(error);

      toast.error("Unable to copy message");
    }
  };

  /* =========================================================
     WHATSAPP
  ========================================================= */

  const handleWhatsAppShare = () => {
    if (!generatedMessage) {
      toast.error("No message available");
      return;
    }

    const encodedMessage = encodeURIComponent(generatedMessage);

    window.open(
      `https://wa.me/?text=${encodedMessage}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  /* =========================================================
     DATE
  ========================================================= */

  const todayLabel = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return (
    <div
      className="
        relative
        flex
        min-h-screen
        w-full
        flex-col

        overflow-visible

        rounded-[20px]
        border
        border-[#e5e1ef]

        bg-gradient-to-br
        from-[#f5f3fb]
        via-[#faf9fc]
        to-[#eef5f7]

        shadow-[0_16px_44px_rgba(89,72,153,0.10)]

        sm:rounded-[24px]

        md:h-[calc(100dvh-70px)]
        md:min-h-0
        md:overflow-hidden

        xl:rounded-[28px]
      "
    >
      <MarketingHeader todayLabel={todayLabel} />

      <MobileTabletMenu
        menus={studioMenus}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      <div
        className="
          flex
          flex-1
          flex-col
          overflow-visible

          md:min-h-0
          md:flex-row
          md:overflow-hidden
        "
      >
        <DesktopSidebar
          menus={studioMenus}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        <main
          className="
            min-w-0
            w-full
            flex-1
            overflow-visible

            px-3
            py-3
            pb-12

            sm:px-4
            sm:py-4

            md:min-h-0
            md:overflow-y-auto
            md:overflow-x-hidden
            md:overscroll-contain
            md:pb-6

            lg:px-5
            xl:px-5
          "
        >
          <motion.div
            variants={pageVariant}
            initial="hidden"
            animate="visible"
            className="
              mx-auto
              w-full
              max-w-[1450px]
            "
          >
            <ActiveToolIntro menu={activeMenu} />

            <AnimatePresence mode="wait">
              {/* =========================
                  MESSAGES
              ========================= */}

              {activeTab === "messages" && (
                <motion.div
                  key="messages"
                  variants={workspaceVariant}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <MessageStudio
                    editorRef={editorRef}
                    message={generatedMessage}
                    setMessage={setGeneratedMessage}
                    isEdited={isEdited}
                    hasSavedTemplate={hasSavedTemplate}
                    isLoading={messageLoading}
                    isSaving={templateSaving}
                    onReset={() => loadDailyMessage(true)}
                    onSaveForFuture={handleSaveForFuture}
                    onCopy={handleCopy}
                    onWhatsapp={handleWhatsAppShare}
                  />
                </motion.div>
              )}

              {/* =========================
                  POSTS
              ========================= */}

              {activeTab === "posts" && (
                <motion.div
                  key="posts"
                  variants={workspaceVariant}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                 <ComingStudio
  icon={<FaImage />}
  eyebrow="Daily Creative"
  title="Create your daily jewellery post"
  description="Create beautiful branded daily greetings, jewellery posts and social media creatives for WhatsApp, Instagram and Facebook."
  features={[
    {
      icon: <FaPalette />,
      title: "Beautiful Templates",
      text: "Premium jewellery-focused daily post designs.",
    },
    {
      icon: <FaMagic />,
      title: "Fresh Content",
      text: "Create different daily greetings and branded content.",
    },
    {
      icon: <FaShareAlt />,
      title: "Ready to Share",
      text: "Create content for your social platforms.",
    },
  ]}
/>
                </motion.div>
              )}

              {/* =========================
                  FESTIVAL
              ========================= */}

             {/* LEADS */}

{activeTab === "leads" && (
  <motion.div
    key="leads"
    variants={workspaceVariant}
    initial="initial"
    animate="animate"
    exit="exit"
  >
    <ComingStudio
      icon={<FaBullhorn />}
      eyebrow="Lead Management"
      title="Turn marketing enquiries into customers"
      description="Soon you will be able to manage customer enquiries from Instagram, Facebook, WhatsApp, exhibitions and other marketing channels in one place."
      features={[
        {
          icon: <FaInstagram />,
          title: "Capture Enquiries",
          text: "Keep social and marketing enquiries organized.",
        },
        {
          icon: <FaEdit />,
          title: "Track Follow-ups",
          text: "Know which customer needs your attention next.",
        },
        {
          icon: <FaCheckCircle />,
          title: "Track Conversions",
          text: "See which enquiries finally convert into customers.",
        },
      ]}
    />
  </motion.div>
)}

              {/* =========================
                  CHANNELS - FUTURE
              ========================= */}

              {activeTab === "channels" && (
                <motion.div
                  key="channels"
                  variants={workspaceVariant}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <SocialStudio />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

/* =========================================================
   HEADER
========================================================= */

const MarketingHeader = ({ todayLabel }) => {
  return (
    <motion.header
      initial={{
        opacity: 0,
        y: -8,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      className="
        relative
        shrink-0
        overflow-hidden
        border-b
        border-[#e6e1ef]

        bg-gradient-to-r
        from-[#f1edfb]
        via-[#faf8fd]
        to-[#eff6f3]

        px-3
        py-3

        sm:px-5
        sm:py-3.5

        lg:px-6
      "
    >
      <motion.div
        animate={{
          x: [0, 10, 0],
          y: [0, -4, 0],
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
          pointer-events-none
          absolute
          -left-14
          -top-16
          h-40
          w-40
          rounded-full
          bg-violet-300/25
          blur-3xl
        "
      />

      <motion.div
        animate={{
          x: [0, -8, 0],
          y: [0, 4, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
          pointer-events-none
          absolute
          right-0
          top-0
          h-36
          w-36
          rounded-full
          bg-pink-300/20
          blur-3xl
        "
      />

      <div
        className="
          relative
          z-10
          flex
          items-center
          justify-between
          gap-3
        "
      >
        <div
          className="
            flex
            min-w-0
            items-center
            gap-3
          "
        >
          <motion.div
            whileHover={{
              rotate: -5,
              scale: 1.04,
            }}
            className="
              grid
              h-11
              w-11
              shrink-0
              place-items-center
              rounded-2xl

              bg-gradient-to-br
              from-[#7657d3]
              via-[#8e70de]
              to-[#d46cb5]

              text-base
              text-white

              shadow-lg
              shadow-violet-200/70

              sm:h-12
              sm:w-12
              sm:text-lg
            "
          >
            <FaBullhorn />
          </motion.div>

          <div className="min-w-0">
            <div
              className="
                flex
                items-center
                gap-2
              "
            >
              <h1
                className="
                  truncate
                  text-lg
                  font-semibold
                  tracking-tight
                  text-slate-900

                  sm:text-xl
                  lg:text-2xl
                "
              >
                Marketing Studio
              </h1>

              <span
                className="
                  hidden
                  rounded-full
                  bg-white/80
                  px-2.5
                  py-1
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-wider
                  text-violet-600
                  ring-1
                  ring-violet-100

                  sm:inline-flex
                "
              >
                Jewellery
              </span>
            </div>

            <p
              className="
                mt-0.5
                truncate
                text-[11px]
                text-slate-500

                sm:text-xs
              "
            >
              Create • Customize • Share customer marketing
            </p>
          </div>
        </div>

        <div
          className="
            flex
            shrink-0
            items-center
            gap-2
          "
        >
          <div
            className="
              hidden
              items-center
              gap-2
              rounded-2xl
              bg-white/80
              px-3
              py-2
              ring-1
              ring-[#e6e1ef]
              shadow-sm

              xl:flex
            "
          >
            <div
              className="
                grid
                h-8
                w-8
                place-items-center
                rounded-xl
                bg-emerald-50
                text-emerald-600
              "
            >
              <FaBolt className="text-xs" />
            </div>

            <div>
              <p
                className="
                  text-[10px]
                  uppercase
                  tracking-wide
                  text-slate-400
                "
              >
                Available Now
              </p>

              <p
                className="
                  text-[11px]
                  font-semibold
                  text-slate-700
                "
              >
                Rate Message
              </p>
            </div>
          </div>

          <span
            className="
              hidden
              rounded-full
              bg-white/85
              px-3
              py-2
              text-[11px]
              font-medium
              text-slate-500
              ring-1
              ring-[#e7e2ef]

              sm:inline-flex
            "
          >
            {todayLabel}
          </span>
        </div>
      </div>
    </motion.header>
  );
};

/* =========================================================
   MOBILE + TABLET MENU
========================================================= */

const MobileTabletMenu = ({ menus, activeTab, setActiveTab }) => {
  return (
    <div
      className="
        shrink-0
        border-b
        border-[#ebe5f3]
        bg-[#f8f6fb]/95
        px-2.5
        py-2.5
        backdrop-blur

        xl:hidden
      "
    >
      <div
        className="
          flex
          gap-2
          overflow-x-auto
          px-0.5
          pb-0.5

          [-ms-overflow-style:none]
          [scrollbar-width:none]
          [&::-webkit-scrollbar]:hidden
        "
      >
        {menus.map((item) => {
          const Icon = item.icon;
          const active = activeTab === item.key;

          return (
            <motion.button
              key={item.key}
              type="button"
              whileTap={{
                scale: 0.97,
              }}
              onClick={() => setActiveTab(item.key)}
              className={[
                `
                  relative
                  flex
                  min-h-[42px]
                  shrink-0
                  items-center
                  gap-2
                  rounded-xl
                  px-3.5
                  py-2.5
                  transition
                `,

                active
                  ? `
                      bg-gradient-to-r
                      from-[#7657d3]
                      to-[#9677e4]
                      text-white
                      shadow-md
                      shadow-violet-200
                    `
                  : `
                      bg-white/80
                      text-slate-600
                      ring-1
                      ring-[#e8e2ef]
                    `,
              ].join(" ")}
            >
              <Icon className="text-xs" />

              <span
                className="
                  whitespace-nowrap
                  text-[11px]
                  font-semibold
                "
              >
                {item.shortTitle}
              </span>

              {item.status === "Live" && (
                <span
                  className={[
                    "h-1.5 w-1.5 rounded-full",

                    active ? "bg-emerald-300" : "bg-emerald-500",
                  ].join(" ")}
                />
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

/* =========================================================
   DESKTOP SIDEBAR
========================================================= */

const DesktopSidebar = ({ menus, activeTab, setActiveTab }) => {
  return (
    <aside
      className="
        hidden
        w-[265px]
        shrink-0
        border-r
        border-[#e8e1f0]

        bg-gradient-to-b
        from-[#f6f2fb]
        via-[#f7f4fa]
        to-[#f1f5f6]

        p-4

        xl:flex
        xl:min-h-0
        xl:flex-col
        xl:overflow-hidden
      "
    >
      <div
        className="
          mb-4
          shrink-0
          px-2
        "
      >
        <p
          className="
            text-[11px]
            font-bold
            uppercase
            tracking-[0.16em]
            text-slate-400
          "
        >
          Studio Tools
        </p>

        <p
          className="
            mt-1
            text-[11px]
            text-slate-500
          "
        >
          Choose what you want to create
        </p>
      </div>

      <div
        className="
          min-h-0
          flex-1
          space-y-2
          overflow-y-auto
          pr-1

          [scrollbar-width:none]
          [&::-webkit-scrollbar]:hidden
        "
      >
        {menus.map((item) => {
          const Icon = item.icon;
          const active = activeTab === item.key;

          return (
            <motion.button
              key={item.key}
              type="button"
              whileHover={{
                x: active ? 0 : 3,
              }}
              whileTap={{
                scale: 0.99,
              }}
              onClick={() => setActiveTab(item.key)}
              className={[
                `
                  group
                  relative
                  flex
                  w-full
                  items-center
                  gap-3
                  overflow-hidden
                  rounded-2xl
                  px-3
                  py-3.5
                  text-left
                  transition
                `,

                active
                  ? `
                      bg-gradient-to-r
                      from-[#7458cf]
                      via-[#866fd9]
                      to-[#9d84e8]
                      text-white
                      shadow-lg
                      shadow-violet-200/70
                    `
                  : `
                      bg-white/60
                      text-slate-600
                      ring-1
                      ring-[#ebe4f0]
                      hover:bg-white
                      hover:shadow-sm
                    `,
              ].join(" ")}
            >
              {active && (
                <motion.div
                  layoutId="marketing-menu-highlight"
                  className="
                    absolute
                    inset-0
                    bg-gradient-to-r
                    from-[#7458cf]
                    via-[#866fd9]
                    to-[#9d84e8]
                  "
                />
              )}

              <div
                className={[
                  `
                    relative
                    z-10
                    grid
                    h-10
                    w-10
                    shrink-0
                    place-items-center
                    rounded-xl
                    transition
                  `,

                  active
                    ? `
                        bg-white/15
                        text-white
                      `
                    : `
                        bg-[#f0ebfa]
                        text-violet-600
                        group-hover:bg-violet-100
                      `,
                ].join(" ")}
              >
                <Icon className="text-sm" />
              </div>

              <div
                className="
                  relative
                  z-10
                  min-w-0
                  flex-1
                "
              >
                <div
                  className="
                    flex
                    items-center
                    gap-2
                  "
                >
                  <p
                    className="
                      truncate
                      text-[12px]
                      font-semibold
                    "
                  >
                    {item.title}
                  </p>

                  {item.status === "Live" && (
                    <span
                      className="
                        h-1.5
                        w-1.5
                        shrink-0
                        rounded-full
                        bg-emerald-300
                      "
                    />
                  )}
                </div>

                <p
                  className={[
                    "mt-0.5 truncate text-[10px]",

                    active ? "text-violet-100" : "text-slate-400",
                  ].join(" ")}
                >
                  {item.subtitle}
                </p>
              </div>

              <FaChevronRight
                className={[
                  "relative z-10 text-[9px]",

                  active ? "text-white/60" : "text-slate-300",
                ].join(" ")}
              />
            </motion.button>
          );
        })}
      </div>

      <motion.div
        initial={{
          opacity: 0,
          y: 8,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          delay: 0.25,
        }}
        whileHover={{
          y: -2,
          scale: 1.01,
        }}
        className="
          mt-4
          shrink-0
          overflow-hidden
          rounded-2xl

          bg-gradient-to-br
          from-[#f5edf9]
          via-[#faf7fc]
          to-[#edf5f8]

          p-4
          ring-1
          ring-[#e7dff0]

          shadow-sm
          shadow-violet-100/40
        "
      >
        <div
          className="
            grid
            h-9
            w-9
            place-items-center
            rounded-xl
            bg-white
            text-violet-600
            shadow-sm
          "
        >
          <FaMagic className="text-xs" />
        </div>

        <p
          className="
            mt-3
            text-[12px]
            font-semibold
            text-slate-700
          "
        >
          Marketing made simple
        </p>

        <p
          className="
            mt-1
            text-[10px]
            leading-5
            text-slate-500
          "
        >
          Create customer-ready marketing content directly from your jewellery
          software.
        </p>
      </motion.div>
    </aside>
  );
};

/* =========================================================
   ACTIVE TOOL INTRO
========================================================= */

const ActiveToolIntro = ({ menu }) => {
  if (!menu) {
    return null;
  }

  const Icon = menu.icon;

  return (
    <motion.div
      variants={fadeUp}
      className="
        mb-3
        flex
        items-start
        gap-3

        rounded-[20px]
        border
        border-[#ebe5f2]

        bg-gradient-to-r
        from-[#f8f5fc]
        via-[#faf9fb]
        to-[#f2f7f4]

        p-3

        shadow-sm
        shadow-violet-100/20

        sm:items-center
        sm:p-4

        xl:hidden
      "
    >
      <div
        className="
          grid
          h-11
          w-11
          shrink-0
          place-items-center
          rounded-2xl

          bg-gradient-to-br
          from-[#eee8fb]
          to-[#fbeaf2]

          text-sm
          text-violet-600

          ring-1
          ring-[#e6dff1]
        "
      >
        <Icon />
      </div>

      <div className="min-w-0">
        <div
          className="
            flex
            flex-wrap
            items-center
            gap-2
          "
        >
          <h2
            className="
              text-[14px]
              font-semibold
              text-slate-900
              sm:text-[15px]
            "
          >
            {menu.title}
          </h2>

          {menu.status === "Live" && (
            <span
              className="
                inline-flex
                items-center
                gap-1.5
                rounded-full
                bg-emerald-50
                px-2.5
                py-1
                text-[9px]
                font-bold
                text-emerald-600
                ring-1
                ring-emerald-100
              "
            >
              <span
                className="
                  h-1.5
                  w-1.5
                  rounded-full
                  bg-emerald-500
                "
              />

              LIVE
            </span>
          )}
        </div>

        <p
          className="
            mt-1
            line-clamp-2
            text-[11px]
            leading-5
            text-slate-500

            sm:text-xs
          "
        >
          {menu.description}
        </p>
      </div>
    </motion.div>
  );
};

/* =========================================================
   MESSAGE STUDIO
========================================================= */

const MessageStudio = ({
  editorRef,

  message,
  setMessage,

  isEdited,
  hasSavedTemplate,

  isLoading,
  isSaving,

  onReset,
  onSaveForFuture,

  onCopy,
  onWhatsapp,
}) => {
  const [showPreview, setShowPreview] = useState(false);

  const characterCount = message?.length || 0;

  const getStatus = () => {
    if (isEdited) {
      return {
        label: "Unsaved Changes",

        classes:
          "bg-amber-50 text-amber-600 ring-1 ring-amber-100",
      };
    }

    if (hasSavedTemplate) {
      return {
        label: "Saved Format",

        classes:
          "bg-violet-50 text-violet-600 ring-1 ring-violet-100",
      };
    }

    return {
      label: "Default Format",

      classes:
        "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100",
    };
  };

  const status = getStatus();

  return (
    <div
      className="
        grid
        w-full
        min-w-0
        grid-cols-1
        gap-4

        xl:grid-cols-[minmax(0,1fr)_330px]

        2xl:grid-cols-[minmax(0,1fr)_350px]
      "
    >
      <motion.section
        variants={fadeUp}
        className="
          min-w-0
          overflow-hidden

          rounded-[22px]

          border
          border-[#e8e1f0]

          bg-gradient-to-br
          from-[#faf8fc]
          via-[#fdfdfe]
          to-[#f7f9f8]

          shadow-[0_12px_30px_rgba(129,110,170,0.08)]

          sm:rounded-[26px]
        "
      >
        {/* HEADER */}

        <div
          className="
            flex
            flex-col
            gap-3

            border-b
            border-[#ece5f2]

            bg-gradient-to-r
            from-[#f4effa]
            via-[#faf8fd]
            to-[#f3f7f4]

            px-3
            py-3

            sm:flex-row
            sm:items-center
            sm:justify-between
            sm:px-5
            sm:py-4
          "
        >
          <div
            className="
              flex
              min-w-0
              items-center
              gap-3
            "
          >
            <div
              className="
                grid
                h-10
                w-10
                shrink-0
                place-items-center
                rounded-2xl

                bg-emerald-50

                text-sm
                text-emerald-600

                ring-1
                ring-emerald-100
              "
            >
              <FaWhatsapp />
            </div>

            <div className="min-w-0">
              <div
                className="
                  flex
                  flex-wrap
                  items-center
                  gap-2
                "
              >
                <p
                  className="
                    text-[14px]
                    font-semibold
                    text-slate-800
                  "
                >
                  Daily Rate Message
                </p>

                {!isLoading && (
                  <motion.span
                    key={status.label}
                    initial={{
                      opacity: 0,
                      scale: 0.95,
                    }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                    }}
                    className={[
                      `
                        rounded-full
                        px-2.5
                        py-1
                        text-[9px]
                        font-bold
                        uppercase
                        tracking-wide
                      `,

                      status.classes,
                    ].join(" ")}
                  >
                    {status.label}
                  </motion.span>
                )}
              </div>

              <p
                className="
                  mt-1
                  text-[11px]
                  text-slate-500
                "
              >
                Today's latest Gold & Silver rates stay updated automatically
              </p>
            </div>
          </div>

          <div
            className="
              grid
              grid-cols-2
              gap-2

              sm:flex
              sm:items-center
            "
          >
            <motion.button
              type="button"
              whileTap={{
                scale: 0.97,
              }}
              onClick={() => setShowPreview(true)}
              className="
                inline-flex
                min-h-[42px]
                items-center
                justify-center
                gap-2

                rounded-xl

                bg-white/90

                px-3

                text-[11px]
                font-semibold
                text-violet-600

                ring-1
                ring-violet-100

                shadow-sm

                xl:hidden
              "
            >
              <FaEye />

              Preview
            </motion.button>

            <motion.button
              type="button"
              whileHover={{
                scale: 1.02,
              }}
              whileTap={{
                scale: 0.97,
              }}
              disabled={isLoading}
              onClick={onReset}
              className="
                inline-flex
                min-h-[42px]
                items-center
                justify-center
                gap-2

                rounded-xl

                bg-white/90

                px-3

                text-[11px]
                font-semibold
                text-slate-600

                ring-1
                ring-[#e5dfea]

                shadow-sm

                transition

                hover:text-violet-600
                hover:ring-violet-200

                disabled:opacity-50
              "
            >
              <FaRedoAlt className={isLoading ? "animate-spin" : ""} />

              <span>
                {hasSavedTemplate ? "Restore Saved" : "Restore Default"}
              </span>
            </motion.button>
          </div>
        </div>

        {/* BODY */}

        <div
          className="
            p-3
            sm:p-5
          "
        >
          <AnimatePresence mode="wait">
            {!isLoading && (
              <motion.div
                key={
                  hasSavedTemplate
                    ? "saved-template"
                    : "first-template"
                }
                initial={{
                  opacity: 0,
                  y: -4,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                }}
                className={[
                  `
                    mb-4
                    flex
                    items-start
                    gap-3

                    rounded-2xl

                    px-3
                    py-3

                    ring-1
                  `,

                  hasSavedTemplate
                    ? `
                        bg-violet-50/70
                        ring-violet-100
                      `
                    : `
                        bg-amber-50/70
                        ring-amber-100
                      `,
                ].join(" ")}
              >
                <div
                  className={[
                    `
                      mt-0.5

                      grid
                      h-8
                      w-8
                      shrink-0
                      place-items-center

                      rounded-xl

                      bg-white

                      text-[12px]

                      shadow-sm
                    `,

                    hasSavedTemplate
                      ? "text-violet-600"
                      : "text-amber-600",
                  ].join(" ")}
                >
                  {hasSavedTemplate ? <FaCheckCircle /> : <FaMagic />}
                </div>

                <div className="min-w-0">
                  <p
                    className={[
                      `
                        text-[12px]
                        font-semibold
                      `,

                      hasSavedTemplate
                        ? "text-violet-700"
                        : "text-amber-700",
                    ].join(" ")}
                  >
                    {hasSavedTemplate
                      ? "Your daily format is saved"
                      : "Set up your daily message once"}
                  </p>

                  <p
                    className="
                      mt-1

                      text-[11px]
                      leading-5
                      text-slate-500
                    "
                  >
                    {hasSavedTemplate
                      ? "The same wording will automatically appear with each day's latest Gold & Silver rates."
                      : "Customize this once and choose Save for Future. After that only the daily rates will update automatically."}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* EDITOR INFO */}

          <div
            className="
              mb-2
              flex
              items-center
              justify-between
              gap-2
            "
          >
            <div
              className="
                flex
                items-center
                gap-2

                text-[11px]
                text-slate-500
              "
            >
              <FaEdit />

              <span>Tap inside and edit anything</span>
            </div>

            <div
              className="
                shrink-0

                rounded-full

                bg-white/90

                px-2.5
                py-1

                text-[10px]
                font-medium
                text-slate-400

                ring-1
                ring-[#ece7f0]
              "
            >
              {characterCount} chars
            </div>
          </div>

          {/* EDITOR */}

          <div
            className="
              relative
              overflow-hidden

              rounded-[20px]

              border
              border-[#e4dced]

              bg-gradient-to-br
              from-[#eee9f5]
              via-[#f7f4f9]
              to-[#f2f4f5]

              sm:rounded-[24px]
            "
          >
            <div
              className="
                pointer-events-none
                absolute
                right-[-50px]
                top-[-60px]
                h-40
                w-40
                rounded-full
                bg-violet-200/35
                blur-3xl
              "
            />

            <div
              className="
                pointer-events-none
                absolute
                bottom-[-60px]
                left-[-40px]
                h-36
                w-36
                rounded-full
                bg-pink-100/45
                blur-3xl
              "
            />

            {isLoading ? (
              <div
                className="
                  relative
                  z-10

                  grid
                  h-[300px]
                  place-items-center

                  sm:h-[330px]
                  lg:h-[340px]
                "
              >
                <div className="text-center">
                  <Spinner
                    size={28}
                    className="text-violet-500"
                  />

                  <p
                    className="
                      mt-3
                      text-[13px]
                      font-semibold
                      text-slate-600
                    "
                  >
                    Preparing today's message
                  </p>

                  <p
                    className="
                      mt-1
                      text-[11px]
                      text-slate-400
                    "
                  >
                    Adding latest metal rates...
                  </p>
                </div>
              </div>
            ) : (
              <textarea
                ref={editorRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                spellCheck={false}
                placeholder="Your marketing message will appear here..."
                className="
                  relative
                  z-10

                  h-[300px]
                  w-full

                  resize-none

                  bg-transparent

                  p-4

                  text-[13px]
                  leading-7
                  text-slate-700

                  outline-none

                  sm:h-[330px]
                  sm:p-5
                  sm:text-[14px]
                  sm:leading-7

                  lg:h-[340px]
                "
              />
            )}
          </div>

          {/* UNSAVED INFO */}

          <AnimatePresence>
            {isEdited && !isLoading && (
              <motion.div
                initial={{
                  opacity: 0,
                  y: -4,
                  height: 0,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  height: "auto",
                }}
                exit={{
                  opacity: 0,
                  height: 0,
                }}
                className="
                  mt-3

                  flex
                  items-start
                  gap-2

                  rounded-xl

                  bg-amber-50/80

                  px-3
                  py-2.5

                  text-[11px]
                  leading-5
                  text-amber-700

                  ring-1
                  ring-amber-100
                "
              >
                <FaEdit className="mt-1 shrink-0" />

                <span>
                  These changes are temporary. Share them for today, or use
                  Save for Future to make them your new daily format.
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ACTION BAR */}

          <div
            className="
              mt-4

              rounded-2xl

              bg-[#f1eff4]

              p-2.5

              ring-1
              ring-[#e7e1ea]
            "
          >
            <div
              className="
                mb-2.5

                hidden
                items-center
                gap-2

                px-1

                text-[10px]
                text-slate-500

                sm:flex
              "
            >
              <FaRegSmileBeam className="shrink-0 text-violet-500" />

              <span>
                {isEdited
                  ? "Temporary changes won't affect future messages unless you save them."
                  : hasSavedTemplate
                    ? "Your saved format automatically uses today's latest rates."
                    : "Customize once and save it as your future daily format."}
              </span>
            </div>

            <div
              className="
                grid
                grid-cols-1
                gap-2.5

                sm:grid-cols-3
              "
            >
              <motion.button
                type="button"
                whileHover={{
                  y: -1,
                  scale: 1.01,
                }}
                whileTap={{
                  scale: 0.97,
                }}
                disabled={
                  !message ||
                  isLoading ||
                  isSaving ||
                  (hasSavedTemplate && !isEdited)
                }
                onClick={onSaveForFuture}
                className="
                  inline-flex
                  min-h-[46px]
                  items-center
                  justify-center
                  gap-2

                  rounded-xl

                  bg-gradient-to-r
                  from-[#7456ca]
                  via-[#866cda]
                  to-[#9a79e4]

                  px-3
                  py-2.5

                  text-[12px]
                  font-semibold
                  text-white

                  shadow-md
                  shadow-violet-200

                  transition

                  hover:shadow-lg

                  disabled:cursor-not-allowed
                  disabled:opacity-40

                  sm:min-h-[42px]
                "
              >
                {isSaving ? (
                  <>
                    <Spinner size={13} />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Save for Future
                  </>
                )}
              </motion.button>

              <motion.button
                type="button"
                whileHover={{
                  y: -1,
                }}
                whileTap={{
                  scale: 0.97,
                }}
                disabled={!message || isLoading}
                onClick={onCopy}
                className="
                  inline-flex
                  min-h-[46px]
                  items-center
                  justify-center
                  gap-2

                  rounded-xl

                  bg-white

                  px-3
                  py-2.5

                  text-[12px]
                  font-semibold
                  text-slate-700

                  ring-1
                  ring-[#e0dbe6]

                  shadow-sm

                  transition

                  hover:text-violet-600
                  hover:ring-violet-200

                  disabled:opacity-40

                  sm:min-h-[42px]
                "
              >
                <FaCopy />

                Copy Message
              </motion.button>

              <motion.button
                type="button"
                whileHover={{
                  y: -1,
                  scale: 1.01,
                }}
                whileTap={{
                  scale: 0.97,
                }}
                disabled={!message || isLoading}
                onClick={onWhatsapp}
                className="
                  inline-flex
                  min-h-[46px]
                  items-center
                  justify-center
                  gap-2

                  rounded-xl

                  bg-gradient-to-r
                  from-[#1fba5e]
                  to-[#25D366]

                  px-3
                  py-2.5

                  text-[12px]
                  font-semibold
                  text-white

                  shadow-lg
                  shadow-green-200/70

                  transition

                  hover:shadow-green-300

                  disabled:opacity-40

                  sm:min-h-[42px]
                "
              >
                <FaWhatsapp className="text-sm" />

                Share WhatsApp
              </motion.button>
            </div>
          </div>
        </div>
      </motion.section>

      {/* DESKTOP PREVIEW */}

      <div className="hidden xl:block">
        <CustomerPreview
          message={message}
          isEdited={isEdited}
          hasSavedTemplate={hasSavedTemplate}
        />
      </div>

      {/* MOBILE/TABLET PREVIEW */}

      <AnimatePresence>
        {showPreview && (
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            className="
              fixed
              inset-0
              z-[999]

              flex
              items-end
              justify-center

              bg-slate-900/45

              p-0

              backdrop-blur-[2px]

              sm:items-center
              sm:p-4

              xl:hidden
            "
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setShowPreview(false);
              }
            }}
          >
            <motion.div
              initial={{
                opacity: 0,
                y: 30,
                scale: 0.98,
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                y: 20,
                scale: 0.98,
              }}
              className="
                relative

                max-h-[90dvh]
                w-full

                overflow-y-auto

                rounded-t-[26px]

                bg-[#f3f1f5]

                p-3

                shadow-2xl

                sm:max-w-[430px]
                sm:rounded-[28px]
                sm:p-4
              "
            >
              <div
                className="
                  mb-3
                  flex
                  items-center
                  justify-between
                "
              >
                <div>
                  <p
                    className="
                      text-[14px]
                      font-semibold
                      text-slate-800
                    "
                  >
                    Customer Preview
                  </p>

                  <p
                    className="
                      mt-0.5
                      text-[11px]
                      text-slate-500
                    "
                  >
                    This is how it will look on WhatsApp
                  </p>
                </div>

                <motion.button
                  type="button"
                  whileTap={{
                    scale: 0.94,
                  }}
                  onClick={() => setShowPreview(false)}
                  className="
                    grid
                    h-9
                    w-9
                    place-items-center

                    rounded-xl

                    bg-white

                    text-slate-500

                    ring-1
                    ring-slate-200
                  "
                >
                  <FaTimes />
                </motion.button>
              </div>

              <CustomerPreview
                message={message}
                isEdited={isEdited}
                hasSavedTemplate={hasSavedTemplate}
                modal
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* =========================================================
   CUSTOMER PREVIEW
========================================================= */

const CustomerPreview = ({
  message,
  isEdited,
  hasSavedTemplate,
  modal = false,
}) => {
  return (
    <motion.aside
      variants={fadeUp}
      className={[
        `
          flex
          flex-col
          overflow-hidden

          border
          border-[#e7e1ef]

          bg-gradient-to-br
          from-[#eef3f1]
          via-[#f4f3f4]
          to-[#eceff4]

          shadow-md
          shadow-violet-100/40
        `,

        modal
          ? `
              rounded-[22px]
              p-3
            `
          : `
              h-full
              rounded-[28px]
              p-4
            `,
      ].join(" ")}
    >
      <div
        className="
          flex
          items-center
          justify-between
        "
      >
        <div>
          <div
            className="
              flex
              items-center
              gap-2
            "
          >
            <FaMobileAlt className="text-sm text-violet-500" />

            <p
              className="
                text-[12px]
                font-semibold
                text-slate-700
              "
            >
              Customer Preview
            </p>
          </div>

          <p
            className="
              mt-1
              text-[10px]
              text-slate-400
            "
          >
            Preview before sharing
          </p>
        </div>

        <span
          className="
            rounded-full
            bg-white
            px-2.5
            py-1
            text-[9px]
            font-bold
            text-emerald-600
            ring-1
            ring-emerald-100
          "
        >
          WHATSAPP
        </span>
      </div>

      <div
        className="
          mt-3
          flex
          flex-1
          justify-center
        "
      >
        <motion.div
          whileHover={{
            y: modal ? 0 : -3,
          }}
          className="
            relative
            w-full
            max-w-[295px]

            overflow-hidden

            rounded-[28px]

            bg-[#23262d]

            p-[5px]

            shadow-[0_14px_32px_rgba(73,76,104,0.18)]
          "
        >
          <div
            className="
              flex
              min-h-[380px]
              flex-col

              overflow-hidden

              rounded-[23px]

              bg-[#efe8dd]
            "
          >
            {/* APP HEADER */}

            <div
              className="
                flex
                items-center
                gap-2.5

                bg-[#f4fbf7]

                px-3
                py-3

                shadow-sm
              "
            >
              <div
                className="
                  grid
                  h-9
                  w-9
                  place-items-center

                  rounded-full

                  bg-gradient-to-br
                  from-violet-500
                  to-pink-400

                  text-[11px]
                  font-bold
                  text-white
                "
              >
                J
              </div>

              <div>
                <p
                  className="
                    text-[11px]
                    font-semibold
                    text-slate-700
                  "
                >
                  Jewellery Customers
                </p>

                <p
                  className="
                    text-[9px]
                    text-slate-400
                  "
                >
                  WhatsApp
                </p>
              </div>
            </div>

            {/* CHAT */}

            <div
              className="
                relative
                flex-1
                overflow-y-auto
                p-3
              "
              style={{
                background:
                  "linear-gradient(135deg, #ece4d8 0%, #ede7df 50%, #e6dfd5 100%)",
              }}
            >
              <div
                className="
                  pointer-events-none
                  absolute
                  inset-0
                  opacity-[0.045]
                "
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 20px 20px, #475569 1px, transparent 1px)",

                  backgroundSize: "24px 24px",
                }}
              />

              <div
                className="
                  relative
                  ml-auto

                  max-w-[95%]

                  whitespace-pre-wrap
                  break-words

                  rounded-2xl
                  rounded-tr-sm

                  bg-[#d5f3c8]

                  px-3
                  py-3

                  text-[11px]
                  leading-5
                  text-slate-700

                  shadow-sm
                "
              >
                {message || "Your message preview will appear here."}

                <div
                  className="
                    mt-1.5
                    text-right
                    text-[8px]
                    text-slate-400
                  "
                >
                  09:45 ✓✓
                </div>
              </div>
            </div>

            {/* COMPOSER */}

            <div
              className="
                flex
                items-center
                gap-2

                bg-[#f7f7f7]

                px-2.5
                py-2.5
              "
            >
              <div
                className="
                  flex-1

                  rounded-full

                  bg-white

                  px-3
                  py-2.5

                  text-[10px]
                  text-slate-300

                  shadow-sm
                "
              >
                Message
              </div>

              <div
                className="
                  grid
                  h-8
                  w-8
                  place-items-center

                  rounded-full

                  bg-[#25D366]

                  text-white
                "
              >
                <FaPaperPlane className="text-[10px]" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div
        className="
          mt-3
          grid
          grid-cols-2
          gap-2
        "
      >
        <div
          className="
            rounded-xl

            bg-white/80

            p-2.5

            ring-1
            ring-[#ebe6ee]
          "
        >
          <p
            className="
              text-[9px]
              uppercase
              tracking-wide
              text-slate-400
            "
          >
            Format
          </p>

          <p
            className={[
              `
                mt-1
                text-[11px]
                font-semibold
              `,

              isEdited
                ? "text-amber-600"
                : hasSavedTemplate
                  ? "text-violet-600"
                  : "text-emerald-600",
            ].join(" ")}
          >
            {isEdited
              ? "Unsaved"
              : hasSavedTemplate
                ? "Saved"
                : "Default"}
          </p>
        </div>

        <div
          className="
            rounded-xl

            bg-white/80

            p-2.5

            ring-1
            ring-[#ebe6ee]
          "
        >
          <p
            className="
              text-[9px]
              uppercase
              tracking-wide
              text-slate-400
            "
          >
            Channel
          </p>

          <p
            className="
              mt-1
              text-[11px]
              font-semibold
              text-emerald-600
            "
          >
            WhatsApp
          </p>
        </div>
      </div>
    </motion.aside>
  );
};

/* =========================================================
   COMING SOON STUDIO
========================================================= */

const ComingStudio = ({
  icon,
  eyebrow,
  title,
  description,
  features,
}) => {
  return (
    <div className="space-y-4">
      <motion.div
        variants={fadeUp}
        className="
          relative
          overflow-hidden

          rounded-[24px]

          border
          border-[#e8e1f1]

          bg-gradient-to-br
          from-[#efe8fb]
          via-[#faf7fb]
          to-[#edf5f7]

          p-5

          shadow-lg
          shadow-violet-100/40

          sm:rounded-[28px]
          sm:p-6

          lg:p-8
        "
      >
        <motion.div
          animate={{
            rotate: [0, 5, 0],
            y: [0, -6, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="
            pointer-events-none
            absolute
            -right-10
            -top-12
            h-48
            w-48

            rounded-[50px]

            bg-gradient-to-br
            from-violet-300/35
            to-pink-200/30

            blur-2xl
          "
        />

        <div
          className="
            relative
            z-10
            max-w-2xl
          "
        >
          <div
            className="
              inline-flex
              items-center
              gap-2

              rounded-full

              bg-white/85

              px-3
              py-1.5

              text-[10px]
              font-bold
              uppercase
              tracking-wider
              text-violet-600

              ring-1
              ring-violet-100

              backdrop-blur
            "
          >
            {eyebrow}
          </div>

          <motion.div
            whileHover={{
              rotate: -3,
              scale: 1.03,
            }}
            className="
              mt-5

              grid
              h-12
              w-12
              place-items-center

              rounded-2xl

              bg-white

              text-lg
              text-violet-600

              ring-1
              ring-violet-100

              shadow-lg
              shadow-violet-100
            "
          >
            {icon}
          </motion.div>

          <h2
            className="
              mt-5

              text-xl
              font-semibold
              tracking-tight
              text-slate-900

              sm:text-2xl
              lg:text-[28px]
            "
          >
            {title}
          </h2>

          <p
            className="
              mt-3
              max-w-xl

              text-[12px]
              leading-6
              text-slate-500

              sm:text-[13px]
            "
          >
            {description}
          </p>

          <div
            className="
              mt-5

              inline-flex
              items-center
              gap-2

              rounded-xl

              bg-white/90

              px-3.5
              py-2.5

              text-[11px]
              font-semibold
              text-violet-600

              ring-1
              ring-white

              shadow-sm
            "
          >
            <FaMagic />

            Coming to Marketing Studio

            <FaArrowRight className="text-[9px]" />
          </div>
        </div>
      </motion.div>

      <div
        className="
          grid
          grid-cols-1
          gap-3

          sm:grid-cols-3
        "
      >
        {features.map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{
              opacity: 0,
              y: 12,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              delay: 0.05 + index * 0.06,
            }}
            whileHover={{
              y: -4,
            }}
            className="
              rounded-[20px]

              bg-gradient-to-br
              from-[#f7f3fb]
              to-white

              p-4

              ring-1
              ring-[#e8e2ee]

              shadow-sm

              transition

              hover:ring-violet-200

              hover:shadow-lg
              hover:shadow-violet-100/30

              sm:rounded-[24px]
              sm:p-5
            "
          >
            <div
              className="
                grid
                h-10
                w-10
                place-items-center

                rounded-xl

                bg-gradient-to-br
                from-violet-50
                to-pink-50

                text-sm
                text-violet-600

                ring-1
                ring-violet-100
              "
            >
              {feature.icon}
            </div>

            <p
              className="
                mt-3

                text-[13px]
                font-semibold
                text-slate-700
              "
            >
              {feature.title}
            </p>

            <p
              className="
                mt-1.5

                text-[11px]
                leading-5
                text-slate-500
              "
            >
              {feature.text}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/* =========================================================
   SOCIAL STUDIO
========================================================= */

const SocialStudio = () => {
  const channels = [
    {
      name: "WhatsApp",
      description:
        "Share messages with customers and groups.",
      icon: FaWhatsapp,
      available: true,
      iconClass: "text-emerald-600",
      bgClass:
        "from-[#edf7f0] to-[#f7fbf8]",
    },

    {
      name: "Instagram",
      description:
        "Publish jewellery posts and campaigns.",
      icon: FaInstagram,
      available: false,
      iconClass: "text-pink-600",
      bgClass:
        "from-[#f9eef6] to-[#faf8fd]",
    },

    {
      name: "Facebook",
      description:
        "Share promotions with your followers.",
      icon: FaFacebookF,
      available: false,
      iconClass: "text-blue-600",
      bgClass:
        "from-[#eef4fb] to-[#f8fbff]",
    },
  ];

  return (
    <div>
      <motion.div
        variants={fadeUp}
        className="
          mb-4

          rounded-[22px]

          bg-gradient-to-r
          from-[#f2ebfb]
          via-[#f8f6fb]
          to-[#eef6fb]

          p-4

          ring-1
          ring-[#e8e0f1]

          sm:rounded-[26px]
          sm:p-5
        "
      >
        <div
          className="
            flex
            items-center
            gap-3
          "
        >
          <div
            className="
              grid
              h-11
              w-11
              place-items-center

              rounded-2xl

              bg-white

              text-sm
              text-violet-600

              shadow-sm
            "
          >
            <FaShareAlt />
          </div>

          <div>
            <p
              className="
                text-[14px]
                font-semibold
                text-slate-800
              "
            >
              Your marketing channels
            </p>

            <p
              className="
                mt-1

                text-[11px]
                text-slate-500
              "
            >
              Manage where your content is shared.
            </p>
          </div>
        </div>
      </motion.div>

      <div
        className="
          grid
          grid-cols-1
          gap-3

          sm:grid-cols-2
          lg:grid-cols-3
        "
      >
        {channels.map((channel, index) => {
          const Icon = channel.icon;

          return (
            <motion.div
              key={channel.name}
              initial={{
                opacity: 0,
                y: 10,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: index * 0.06,
              }}
              whileHover={{
                y: -4,
              }}
              className={`
                overflow-hidden

                rounded-[22px]

                bg-gradient-to-br
                ${channel.bgClass}

                p-4

                ring-1
                ring-[#e7e1ee]

                shadow-md

                sm:rounded-[26px]
                sm:p-5
              `}
            >
              <div
                className="
                  flex
                  items-start
                  justify-between
                  gap-3
                "
              >
                <div
                  className="
                    grid
                    h-11
                    w-11
                    place-items-center

                    rounded-2xl

                    bg-white

                    shadow-sm
                  "
                >
                  <Icon
                    className={`
                      text-lg
                      ${channel.iconClass}
                    `}
                  />
                </div>

                <span
                  className={[
                    `
                      rounded-full

                      px-2.5
                      py-1

                      text-[9px]
                      font-bold
                      uppercase
                      tracking-wide
                    `,

                    channel.available
                      ? `
                          bg-emerald-100
                          text-emerald-600
                        `
                      : `
                          bg-white/80
                          text-slate-400
                        `,
                  ].join(" ")}
                >
                  {channel.available ? "Available" : "Coming Soon"}
                </span>
              </div>

              <p
                className="
                  mt-4

                  text-[14px]
                  font-semibold
                  text-slate-800
                "
              >
                {channel.name}
              </p>

              <p
                className="
                  mt-1.5

                  text-[11px]
                  leading-5
                  text-slate-500
                "
              >
                {channel.description}
              </p>

              <div
                className="
                  mt-4

                  flex
                  items-center
                  gap-1.5

                  text-[10px]
                  font-semibold
                  text-violet-500
                "
              >
                {channel.available ? "Ready to use" : "Setup later"}

                <FaArrowRight className="text-[8px]" />
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default Marketing;